import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { NotificationIcon } from "./NotificationIcon";
import { NotificationMessage } from "./NotificationMessage";
import { NotificationActions } from "./NotificationActions";
import { InviteResponseActions } from "./InviteResponseActions";

interface NotificationCardProps {
  notification: Notification;
}

export function NotificationCard({ notification }: NotificationCardProps) {
  const { markAsRead, deleteNotification } = useNotifications();
  const [isHovered, setIsHovered] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleMarkAsRead = () => {
    markAsRead(notification.id);
  };

  const handleDelete = () => {
    setIsDeleting(true);
    deleteNotification(notification.id);
  };

  const formattedDate = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
  });

  return (
    <div
      className={`
        relative p-4 transition-colors hover:bg-muted/30
        ${notification.is_read ? "bg-background" : "bg-primary/5"}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start gap-3">
        <NotificationIcon type={notification.type} />

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-1">
            <h4 className="font-medium text-sm line-clamp-1">
              {notification.title}
            </h4>
            <span className="text-xs text-muted-foreground ml-2 whitespace-nowrap">
              {formattedDate}
            </span>
          </div>

          <NotificationMessage message={notification.message} />

          <InviteResponseActions notification={notification} />

          <NotificationActions
            notification={notification}
            onMarkAsRead={handleMarkAsRead}
          />
        </div>
      </div>

      {isHovered && notification.is_read && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 absolute top-2 right-2 opacity-70 hover:opacity-100"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}

      {!notification.is_read && (
        <div className="absolute top-4 right-4 w-2 h-2 bg-primary rounded-full" />
      )}
    </div>
  );
}
